// Página AdminCategories - Gestión de categorías de productos
// Permite crear, editar y eliminar categorías

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCategories, useCategoryActions } from '../../hooks/useCategories'
import { formatCurrencyInput, parseCurrencyInput } from '../../utils/helpers'
import { uploadImage } from '../../services/cloudinary.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminCategories.css'

/**
 * Página AdminCategories
 * Gestión completa de categorías (CRUD)
 */
function AdminCategories() {
  const { categories, loading, error, refresh } = useCategories(true) // Incluir inactivas
  const { create, update, remove, loading: actionLoading } = useCategoryActions()
  
  const [showForm, setShowForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
    profitPercentage: '',
    preparationCosts: [], // Costos de preparación
    imageUrl: null // URL de la imagen representativa
  })
  const [formErrors, setFormErrors] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [notification, setNotification] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  
  // Estado para el formulario de nuevo costo
  const [newCost, setNewCost] = useState({ name: '', amount: '', description: '' })
  const [editingCostIndex, setEditingCostIndex] = useState(null)
  const [editingFields, setEditingFields] = useState(new Set())

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Validar formulario
  const validate = () => {
    const errors = {}
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Guardar categoría (crear o actualizar)
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    try {
      if (editingCategory) {
        await update(editingCategory.id, formData)
        setNotification({
          type: 'success',
          message: 'Categoría actualizada correctamente'
        })
      } else {
        await create(formData)
        setNotification({
          type: 'success',
          message: 'Categoría creada correctamente'
        })
      }
      resetForm()
      refresh()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Manejar cambio de imagen
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      setNotification({
        type: 'error',
        message: 'Por favor selecciona un archivo de imagen válido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setUploadingImage(true)
    try {
      // Crear preview local inmediato
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)

      // Subir a Cloudinary
      const uploadResult = await uploadImage(file, 'categories')
      
      // Actualizar formData con la URL de Cloudinary
      setFormData(prev => ({
        ...prev,
        imageUrl: uploadResult.url
      }))
      
      setImagePreview(uploadResult.url)
      
      setNotification({
        type: 'success',
        message: 'Imagen subida correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error subiendo imagen:', error)
      setNotification({
        type: 'error',
        message: `Error al subir imagen: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
      e.target.value = '' // Limpiar input
    }
  }

  // Eliminar imagen
  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageUrl: null
    }))
    setImagePreview(null)
  }

  // Editar categoría
  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name || '',
      description: category.description || '',
      active: category.active !== false,
      profitPercentage: category.profitPercentage !== null && category.profitPercentage !== undefined 
        ? category.profitPercentage.toString() 
        : '',
      preparationCosts: category.preparationCosts || [],
      imageUrl: category.imageUrl || null
    })
    setImagePreview(category.imageUrl || null)
    setShowForm(true)
  }
  
  // Agregar costo de preparación
  const handleAddCost = () => {
    if (!newCost.name.trim() || !newCost.amount) {
      setNotification({
        type: 'error',
        message: 'El nombre y el monto son requeridos'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    
    const cost = {
      id: Date.now().toString(),
      name: newCost.name.trim(),
      amount: parseCurrencyInput(newCost.amount),
      description: newCost.description.trim() || ''
    }
    
    setFormData(prev => ({
      ...prev,
      preparationCosts: [...prev.preparationCosts, cost]
    }))
    
    setNewCost({ name: '', amount: '', description: '' })
  }
  
  // Editar costo de preparación
  const handleEditCost = (index) => {
    const cost = formData.preparationCosts[index]
    setNewCost({
      name: cost.name,
      amount: formatCurrencyInput(cost.amount),
      description: cost.description || ''
    })
    setEditingCostIndex(index)
  }
  
  // Guardar edición de costo
  const handleSaveCost = () => {
    if (!newCost.name.trim() || !newCost.amount) {
      setNotification({
        type: 'error',
        message: 'El nombre y el monto son requeridos'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    
    const updatedCost = {
      ...formData.preparationCosts[editingCostIndex],
      name: newCost.name.trim(),
      amount: parseCurrencyInput(newCost.amount),
      description: newCost.description.trim() || ''
    }
    
    setFormData(prev => ({
      ...prev,
      preparationCosts: prev.preparationCosts.map((cost, idx) => 
        idx === editingCostIndex ? updatedCost : cost
      )
    }))
    
    setNewCost({ name: '', amount: '', description: '' })
    setEditingCostIndex(null)
  }
  
  // Eliminar costo de preparación
  const handleDeleteCost = (index) => {
    setFormData(prev => ({
      ...prev,
      preparationCosts: prev.preparationCosts.filter((_, idx) => idx !== index)
    }))
  }
  
  // Cancelar edición de costo
  const handleCancelCostEdit = () => {
    setNewCost({ name: '', amount: '', description: '' })
    setEditingCostIndex(null)
  }

  // Eliminar categoría
  const handleDelete = async (id) => {
    try {
      await remove(id)
      setNotification({
        type: 'success',
        message: 'Categoría eliminada correctamente'
      })
      setDeleteConfirm(null)
      refresh()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Resetear formulario
  const resetForm = () => {
    setFormData({ name: '', description: '', active: true, profitPercentage: '', preparationCosts: [], imageUrl: null })
    setEditingCategory(null)
    setShowForm(false)
    setFormErrors({})
    setNewCost({ name: '', amount: '', description: '' })
    setEditingCostIndex(null)
    setImagePreview(null)
  }

  if (loading) {
    return <div className="loading-state">Cargando categorías...</div>
  }

  if (error) {
    return <div className="error-state">Error: {error}</div>
  }

  return (
    <div className="admin-categories-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />
      <div className="categories-header">
        <div>
          <h2>Gestión de Categorías</h2>
          <p>Administra las categorías disponibles para los productos</p>
        </div>
        <div className="header-actions">
          <Link to="/admin/configuraciones">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Button 
            variant="primary" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancelar' : '+ Nueva Categoría'}
          </Button>
        </div>
      </div>

      {/* Formulario de categoría */}
      {showForm && (
        <div className="category-form-section">
          <h3>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</h3>
          <form onSubmit={handleSubmit} className="category-form">
            <div className="form-group">
              <label htmlFor="name">
                Nombre de la Categoría <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Sofás & Sillones"
                className={formErrors.name ? 'error' : ''}
                disabled={actionLoading}
              />
              {formErrors.name && <span className="error-message">{formErrors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descripción opcional de la categoría..."
                rows="3"
                disabled={actionLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryImage">
                Imagen Representativa (opcional)
              </label>
              <div className="image-upload-area">
                {imagePreview ? (
                  <div className="image-preview-container">
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="remove-image-btn"
                      disabled={actionLoading || uploadingImage}
                    >
                      ✕ Eliminar
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      id="categoryImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={actionLoading || uploadingImage}
                      className="file-input"
                    />
                    <label htmlFor="categoryImage" className="file-label">
                      <span className="upload-icon">📷</span>
                      <span>
                        {uploadingImage ? 'Subiendo imagen...' : 'Haz clic para seleccionar una imagen'}
                      </span>
                      <small>Se subirá automáticamente a Cloudinary</small>
                    </label>
                  </>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="profitPercentage">
                % de Ganancia (opcional)
              </label>
              <input
                type="number"
                id="profitPercentage"
                name="profitPercentage"
                value={formData.profitPercentage}
                onChange={handleChange}
                placeholder="Ej: 30"
                min="0"
                max="100"
                step="0.01"
                disabled={actionLoading}
              />
              <small className="form-hint">
                Porcentaje de ganancia para esta categoría. Si no se especifica, se usará la ganancia general configurada.
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  disabled={actionLoading}
                />
                <span>Categoría activa (visible en el catálogo)</span>
              </label>
            </div>

            {/* Sección de Costos de Preparación */}
            <div className="form-section">
              <h4 className="section-title">Costos de Preparación</h4>
              <p className="section-description">
                Estos costos se heredarán a todos los productos de esta categoría. Los productos podrán excluir o agregar costos específicos.
              </p>
              
              {/* Lista de costos existentes */}
              {formData.preparationCosts.length > 0 && (
                <div className="costs-list">
                  <table className="costs-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Monto</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.preparationCosts.map((cost, index) => (
                        <tr key={cost.id || index}>
                          <td>{cost.name}</td>
                          <td>{formatCurrencyInput(cost.amount)}</td>
                          <td>{cost.description || '-'}</td>
                          <td>
                            <div className="cost-actions">
                              <button
                                type="button"
                                onClick={() => handleEditCost(index)}
                                className="action-btn edit-btn"
                                disabled={actionLoading}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCost(index)}
                                className="action-btn delete-btn"
                                disabled={actionLoading}
                                title="Eliminar"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Formulario para agregar/editar costo */}
              <div className="cost-form">
                <div className="cost-form-row">
                  <div className="form-group">
                    <label htmlFor="costName">Nombre del Costo</label>
                    <input
                      type="text"
                      id="costName"
                      value={newCost.name}
                      onChange={(e) => setNewCost(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ej: Montaje, Mantenimiento"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="costAmount">Monto</label>
                    <input
                      type="text"
                      id="costAmount"
                      value={
                        editingFields.has('costAmount')
                          ? (newCost.amount ? `$${newCost.amount.replace('.', ',')}` : '')
                          : (newCost.amount ? formatCurrencyInput(newCost.amount, true) : '')
                      }
                      onChange={(e) => {
                        let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                        if (!cleanedValue) {
                          setNewCost(prev => ({ ...prev, amount: '' }))
                          return
                        }
                        cleanedValue = cleanedValue.replace('.', ',')
                        const parts = cleanedValue.split(',')
                        if (parts.length > 2) {
                          cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                        }
                        setNewCost(prev => ({ ...prev, amount: cleanedValue }))
                      }}
                      onFocus={() => setEditingFields(prev => new Set(prev).add('costAmount'))}
                      onBlur={() => {
                        const numericValue = parseCurrencyInput(newCost.amount || '0')
                        setNewCost(prev => ({ ...prev, amount: numericValue > 0 ? numericValue.toString() : '' }))
                        setEditingFields(prev => {
                          const newSet = new Set(prev)
                          newSet.delete('costAmount')
                          return newSet
                        })
                      }}
                      placeholder="$0"
                      disabled={actionLoading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="costDescription">Descripción (opcional)</label>
                  <input
                    type="text"
                    id="costDescription"
                    value={newCost.description}
                    onChange={(e) => setNewCost(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descripción del costo..."
                    disabled={actionLoading}
                  />
                </div>
                <div className="cost-form-actions">
                  {editingCostIndex !== null ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancelCostEdit}
                        disabled={actionLoading}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleSaveCost}
                        disabled={actionLoading}
                      >
                        Guardar Cambios
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleAddCost}
                      disabled={actionLoading}
                    >
                      + Agregar Costo
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={resetForm} disabled={actionLoading}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={actionLoading}>
                {actionLoading 
                  ? (editingCategory ? 'Guardando...' : 'Creando...') 
                  : (editingCategory ? 'Guardar Cambios' : 'Crear Categoría')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de categorías */}
      <div className="categories-list">
        <h3>Categorías Existentes ({categories.length})</h3>
        {categories.length === 0 ? (
          <div className="empty-state">
            <p>No hay categorías creadas. Crea la primera categoría para comenzar.</p>
          </div>
        ) : (
          <>
            {/* Tabla para desktop */}
            <div className="categories-table-container desktop-view">
              <table className="categories-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>% Ganancia</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td>
                        <strong>{category.name}</strong>
                        <br />
                        <small className="slug-text">/{category.slug || 'sin-slug'}</small>
                      </td>
                      <td>{category.description || '-'}</td>
                      <td>
                        {category.profitPercentage !== null && category.profitPercentage !== undefined 
                          ? `${category.profitPercentage}%` 
                          : <span className="text-muted">General</span>}
                      </td>
                      <td>
                        <span className={`status-badge ${category.active ? 'active' : 'inactive'}`}>
                          {category.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => handleEdit(category)}
                            className="action-btn edit-btn"
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(category.id)}
                            className="action-btn delete-btn"
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards para móviles */}
            <div className="category-card-grid mobile-view">
              {categories.map((category) => (
                <div key={category.id} className="category-card">
                  <div className="card-header">
                    <div>
                      <h4 className="card-category-name">{category.name}</h4>
                      <small className="slug-text">/{category.slug || 'sin-slug'}</small>
                    </div>
                    <span className={`status-badge ${category.active ? 'active' : 'inactive'}`}>
                      {category.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <div className="card-body">
                    {category.description && (
                      <div className="card-info-row">
                        <span className="info-label">Descripción:</span>
                        <span className="info-value">{category.description}</span>
                      </div>
                    )}
                    <div className="card-info-row">
                      <span className="info-label">% Ganancia:</span>
                      <span className="info-value">
                        {category.profitPercentage !== null && category.profitPercentage !== undefined 
                          ? `${category.profitPercentage}%` 
                          : <span className="text-muted">General</span>}
                      </span>
                    </div>
                  </div>
                  <div className="card-actions">
                    <button
                      onClick={() => handleEdit(category)}
                      className="action-btn edit-btn"
                      title="Editar"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(category.id)}
                      className="action-btn delete-btn"
                      title="Eliminar"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>¿Eliminar categoría?</h3>
            <p>Esta acción no se puede deshacer. ¿Estás seguro?</p>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => handleDelete(deleteConfirm)}>
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCategories

